import type { HTMLAttributes } from 'react';
import React, { useEffect, useState } from 'react';
import codes from 'country-telephone-data';
import styled from 'styled-components';
import { useI18n } from '@whizz/react-i18n';
import type { ControlProps, MenuListComponentProps, OptionProps, OptionTypeBase } from 'react-select';
import { components } from 'react-select';
import classnames from 'classnames';
import { CountryFlag, MaterialIcon, Selector } from '../../components';
import { CustomMenuWithResults, CustomPhoneCodeOption } from '../../components/selector/components';
import { GLOBAL_ICONS } from '../../util/vars';
import { SELECTOR_ICON, SELECTOR_STUDENT_SEARCH } from '../../util/data-id';

export type CountryValue = {
  value: string,
  label: string,
  iso2: string,
};

type HTMLProps = HTMLAttributes<HTMLDivElement>;
interface Props extends HTMLProps {
  selectedValue: string | null;
  defaultCountry: CountryValue['iso2'];
  handleChange(option: OptionTypeBase): void;
}

const phoneCodesSharedWithOthers = [ 'ca', 'do', 'gg', 'im', 'je', 'pr', 'um' ]; // These countries share codes with UK or US, so remove them

const PhoneCodeSearch = ({
  selectedValue,
  defaultCountry,
  handleChange,
  ...other
}: Props): JSX.Element => {
  const { translate } = useI18n();

  const countryCodeList: CountryValue[] = codes.allCountries
    .filter((countryCodeItem) => {
      // Filter out countries not in our translated list
      if (phoneCodesSharedWithOthers.includes(countryCodeItem.iso2)) {
        return false;
      }
      return !translate(`countries.COUNTRY_${countryCodeItem.iso2.toUpperCase()}`).startsWith('countries');
    })
    .map((countryCodeItem) => {
      const { dialCode, iso2 } = countryCodeItem;
      const countryName = translate(`countries.COUNTRY_${iso2.toUpperCase()}`);
      const value = `+${dialCode}`;

      return {
        value,
        label: countryName,
        iso2,
      };
    });

  const [ usingDefault, setUsingDefault ] = useState(false); // Ideally would derive this
  const [ showSelector, setShowSelector ] = useState(false);

  const toggleShowSelector = () => {
    setShowSelector((showSelector) => !showSelector);
  };

  const handleChangeSelector = (option: OptionTypeBase) => {
    handleChange(option);

    if (option !== null) {
      setShowSelector(false);
      setUsingDefault(false);
    }
  };

  useEffect(() => {
    if (!selectedValue) {
      const option = countryCodeList.find((countryCodeItem) => countryCodeItem.iso2 === defaultCountry);
      handleChange(option as OptionTypeBase);
      setUsingDefault(true);
    }
  }, [ selectedValue, defaultCountry ]);

  const selectedOption = countryCodeList.find((countryOption) => countryOption.value === (selectedValue ?? '+44'));

  return (
    <SearchWrapper {...other as HTMLProps} hasValue={!usingDefault}>
      {selectedOption && (
        <div className={'flex items-center cursor-pointer'} onClick={toggleShowSelector}>
          <CountryFlag iso2={selectedOption.iso2} label={selectedOption.label} className={'mr-2'}/>
          {selectedOption.value}
          <MaterialIcon icon={GLOBAL_ICONS.toggle_off} className={'mr-8'} />
        </div>
      )}
      {showSelector && (
        <div className={'absolute mt-6 w-96 z-10'}>
          <Selector
            id={'phone_code'}
            className={SELECTOR_STUDENT_SEARCH}
            label={'Search country'}
            placeholder={translate('components.SELECTORS.PLACEHOLDERS.COUNTRY_SEARCH')}
            hideLabel
            icon={GLOBAL_ICONS.search}
            menuIsOpen
            onChange={handleChangeSelector}
            options={countryCodeList}
            customComponents={{
              DropdownIndicator: null,
              Control: (props: ControlProps<OptionTypeBase, false>) => {
                return (
                  <components.Control
                    className={classnames({
                      'has_value': props.hasValue,
                    })}
                    {...props}
                  />
                );
              },
              Option: (props: OptionProps<OptionTypeBase, false>) => {
                return <CustomPhoneCodeOption {...props} />;
              },
              MenuList: (props: MenuListComponentProps<OptionTypeBase, false>) => {
                return <CustomMenuWithResults {...props} />;
              },
            }}
          />
        </div>
      )}
    </SearchWrapper>
  );
};

const SearchWrapper = styled('div')<{
  hasValue: boolean;
}>`
  ${(props) => props.hasValue ? `
  [data-id='${SELECTOR_ICON}'] {
    [data-id='whizz_icon-material'] {
      fill: ${props.theme.colors.secondary} !important;
    }
  }
  ` : null}
`;

export default PhoneCodeSearch;
